import { useEffect } from 'react';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PrivacyPolicyModal({ isOpen, onClose }: PrivacyPolicyModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="relative bg-dark-light border border-white/10 rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden shadow-2xl">
        <div className="sticky top-0 bg-dark-light border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-white">
            Политика в отношении обработки персональных данных
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="px-6 py-6 overflow-y-auto max-h-[calc(85vh-80px)] font-body text-sm text-white/70 leading-relaxed">
          <section className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">1. Общие положения</h3>
            <p className="mb-3">
              Настоящая политика обработки персональных данных составлена в соответствии с требованиями Федерального закона от 27.07.2006. № 152-ФЗ «О персональных данных» (далее — Закон о персональных данных) и определяет порядок обработки персональных данных и меры по обеспечению безопасности персональных данных, предпринимаемые ИП Горбатовская Ангелина Игоревна (далее — Оператор).
            </p>
            <p className="mb-3">
              <strong>1.1.</strong> Оператор ставит своей важнейшей целью и условием осуществления своей деятельности соблюдение прав и свобод человека и гражданина при обработке его персональных данных, в том числе защиты прав на неприкосновенность частной жизни, личную и семейную тайну.
            </p>
            <p>
              <strong>1.2.</strong> Настоящая политика Оператора в отношении обработки персональных данных (далее — Политика) применяется ко всей информации, которую Оператор может получить о посетителях веб-сайта https://ga-hr.ru.
            </p>
          </section>

          <section className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">2. Основные понятия, используемые в Политике</h3>
            <p className="mb-2">
              <strong>2.1.</strong> Автоматизированная обработка персональных данных — обработка персональных данных с помощью средств вычислительной техники.
            </p>
            <p className="mb-2">
              <strong>2.2.</strong> Блокирование персональных данных — временное прекращение обработки персональных данных (за исключением случаев, если обработка необходима для уточнения персональных данных).
            </p>
            <p className="mb-2">
              <strong>2.3.</strong> Веб-сайт — совокупность графических и информационных материалов, а также программ для ЭВМ и баз данных, обеспечивающих их доступность в сети интернет по сетевому адресу https://ga-hr.ru.
            </p>
            <p className="mb-2">
              <strong>2.4.</strong> Информационная система персональных данных — совокупность содержащихся в базах данных персональных данных и обеспечивающих их обработку информационных технологий и технических средств.
            </p>
            <p className="mb-2">
              <strong>2.5.</strong> Обезличивание персональных данных — действия, в результате которых невозможно определить без использования дополнительной информации принадлежность персональных данных конкретному Пользователю или иному субъекту персональных данных.
            </p>
            <p className="mb-2">
              <strong>2.6.</strong> Обработка персональных данных — любое действие (операция) или совокупность действий (операций), совершаемых с использованием средств автоматизации или без использования таких средств с персональными данными, включая сбор, запись, систематизацию, накопление, хранение, уточнение (обновление, изменение), извлечение, использование, передачу (распространение, предоставление, доступ), обезличивание, блокирование, удаление, уничтожение персональных данных.
            </p>
            <p className="mb-2">
              <strong>2.7.</strong> Оператор — государственный орган, муниципальный орган, юридическое или физическое лицо, самостоятельно или совместно с другими лицами организующие и/или осуществляющие обработку персональных данных, а также определяющие цели обработки персональных данных, состав персональных данных, подлежащих обработке, действия (операции), совершаемые с персональными данными.
            </p>
            <p className="mb-2">
              <strong>2.8.</strong> Персональные данные — любая информация, относящаяся прямо или косвенно к определенному или определяемому Пользователю веб-сайта https://ga-hr.ru.
            </p>
            <p className="mb-2">
              <strong>2.9.</strong> Персональные данные, разрешенные субъектом персональных данных для распространения, — персональные данные, доступ неограниченного круга лиц к которым предоставлен субъектом персональных данных путем дачи согласия на обработку персональных данных, разрешенных субъектом персональных данных для распространения в порядке, предусмотренном Законом о персональных данных (далее — персональные данные, разрешенные для распространения).
            </p>
            <p className="mb-2">
              <strong>2.10.</strong> Пользователь — любой посетитель веб-сайта https://ga-hr.ru.
            </p>
            <p className="mb-2">
              <strong>2.11.</strong> Предоставление персональных данных — действия, направленные на раскрытие персональных данных определенному лицу или определенному кругу лиц.
            </p>
            <p className="mb-2">
              <strong>2.12.</strong> Распространение персональных данных — любые действия, направленные на раскрытие персональных данных неопределенному кругу лиц (передача персональных данных) или на ознакомление с персональными данными неограниченного круга лиц, в том числе обнародование персональных данных в средствах массовой информации, размещение в информационно-телекоммуникационных сетях или предоставление доступа к персональным данным каким-либо иным способом.
            </p>
            <p className="mb-2">
              <strong>2.13.</strong> Трансграничная передача персональных данных — передача персональных данных на территорию иностранного государства органу власти иностранного государства, иностранному физическому или иностранному юридическому лицу.
            </p>
            <p>
              <strong>2.14.</strong> Уничтожение персональных данных — любые действия, в результате которых персональные данные уничтожаются безвозвратно с невозможностью дальнейшего восстановления содержания персональных данных в информационной системе персональных данных и/или уничтожаются материальные носители персональных данных.
            </p>
          </section>

          <section className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">3. Основные права и обязанности Оператора</h3>
            <p className="mb-2"><strong>3.1.</strong> Оператор имеет право:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>получать от субъекта персональных данных достоверные информацию и/или документы, содержащие персональные данные;</li>
              <li>в случае отзыва субъектом персональных данных согласия на обработку персональных данных, а также, направления обращения с требованием о прекращении обработки персональных данных, Оператор вправе продолжить обработку персональных данных без согласия субъекта персональных данных при наличии оснований, указанных в Законе о персональных данных;</li>
              <li>самостоятельно определять состав и перечень мер, необходимых и достаточных для обеспечения выполнения обязанностей, предусмотренных Законом о персональных данных и принятыми в соответствии с ним нормативными правовыми актами, если иное не предусмотрено Законом о персональных данных или другими федеральными законами.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">4. Основные права и обязанности субъектов персональных данных</h3>
            <p className="mb-2"><strong>4.1.</strong> Субъекты персональных данных имеют право:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>получать информацию, касающуюся обработки его персональных данных, за исключением случаев, предусмотренных федеральными законами;</li>
              <li>требовать от оператора уточнения его персональных данных, их блокирования или уничтожения в случае, если персональные данные являются неполными, устаревшими, неточными, незаконно полученными или не являются необходимыми для заявленной цели обработки;</li>
              <li>отозвать согласие на обработку персональных данных;</li>
              <li>обжаловать действия или бездействие Оператора в уполномоченный орган по защите прав субъектов персональных данных или в судебном порядке.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">5. Принципы обработки персональных данных</h3>
            <p className="mb-2">Обработка персональных данных осуществляется на основе следующих принципов:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>законности и справедливой основы;</li>
              <li>ограничения обработки персональных данных достижением конкретных, заранее определенных и законных целей;</li>
              <li>недопущения обработки персональных данных, несовместимой с целями сбора персональных данных;</li>
              <li>недопущения объединения баз данных, содержащих персональные данные, обработка которых осуществляется в целях, несовместимых между собой;</li>
              <li>обработки только тех персональных данных, которые отвечают целям их обработки;</li>
              <li>соответствия содержания и объема обрабатываемых персональных данных заявленным целям обработки.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">6. Цели обработки персональных данных</h3>
            <p className="mb-3">
              <strong>6.1.</strong> Цель обработки персональных данных Пользователя:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>информирование Пользователя посредством отправки электронных писем;</li>
              <li>предоставление доступа Пользователю к сервисам, информации и/или материалам, содержащимся на веб-сайте;</li>
              <li>связь с Пользователем, в том числе направление уведомлений, запросов и информации, касающихся использования сервисов, оказания услуг, а также обработка запросов и заявок от Пользователя.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">7. Условия обработки персональных данных</h3>
            <p className="mb-3">
              <strong>7.1.</strong> Обработка персональных данных осуществляется с согласия субъекта персональных данных на обработку его персональных данных.
            </p>
            <p className="mb-3">
              <strong>7.2.</strong> Обработка персональных данных необходима для достижения целей, предусмотренных международным договором Российской Федерации или законом, для осуществления возложенных законодательством Российской Федерации на оператора функций, полномочий и обязанностей.
            </p>
          </section>

          <section className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">8. Порядок сбора, хранения, передачи и других видов обработки персональных данных</h3>
            <p className="mb-3">
              Безопасность персональных данных, которые обрабатываются Оператором, обеспечивается путем реализации правовых, организационных и технических мер, необходимых для выполнения в полном объеме требований действующего законодательства в области защиты персональных данных.
            </p>
            <p className="mb-3">
              <strong>8.1.</strong> Оператор обеспечивает сохранность персональных данных и принимает все возможные меры, исключающие доступ к персональным данным неуполномоченных лиц.
            </p>
            <p>
              <strong>8.2.</strong> Персональные данные Пользователя никогда, ни при каких условиях не будут переданы третьим лицам, за исключением случаев, связанных с исполнением действующего законодательства либо в случае, если субъектом персональных данных дано согласие Оператору на передачу данных третьему лицу для исполнения обязательств по гражданско-правовому договору.
            </p>
          </section>

          <section className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">9. Заключительные положения</h3>
            <p className="mb-3">
              <strong>9.1.</strong> Пользователь может получить любые разъяснения по интересующим вопросам, касающимся обработки его персональных данных, обратившись к Оператору с помощью электронной почты info@gahrgroup.com.
            </p>
            <p className="mb-3">
              <strong>9.2.</strong> В данном документе будут отражены любые изменения политики обработки персональных данных Оператором. Политика действует бессрочно до замены ее новой версией.
            </p>
            <p>
              <strong>9.3.</strong> Актуальная версия Политики в свободном доступе расположена в сети Интернет по адресу https://ga-hr.ru.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
